import { useId } from 'react';

import { inputClass } from '../../components/Field';
import {
  formatRetention,
  minutesPerDay,
  retentionHourOptions,
  retentionMinuteOptions,
  retentionPresets,
  type RetentionUnit,
} from './api';

interface Props {
  unit: RetentionUnit;
  days: string;
  hours: string;
  minutes: string;
  onUnit: (unit: RetentionUnit) => void;
  onDays: (days: string) => void;
  onHours: (hours: string) => void;
  onMinutes: (minutes: string) => void;
  readOnly: boolean;
  /** Shown under the fields: "De 5 min a 24 h" or the API's message. */
  error: string | undefined;
}

/** Retention of a recording policy: in days (the options) or in hours and minutes (5 min to 24 h). */
export function RetentionField({
  unit,
  days,
  hours,
  minutes,
  onUnit,
  onDays,
  onHours,
  onMinutes,
  readOnly,
  error,
}: Props) {
  const id = useId();
  const labelId = `${id}-label`;
  const errorId = `${id}-error`;
  const describedBy = error ? errorId : undefined;
  const invalid = error ? true : undefined;
  const dayOptions = [...new Set([...retentionPresets, Number(days)])].sort((a, b) => a - b);
  // A legacy value (outside 0-24 h / steps of 5) stays selectable until it is changed.
  const hourOptions = [...new Set([...retentionHourOptions, Number(hours)])].sort((a, b) => a - b);
  const minuteOptions = [...new Set([...retentionMinuteOptions, Number(minutes)])].sort(
    (a, b) => a - b,
  );
  return (
    <div role="group" aria-labelledby={labelId}>
      <div className="mb-1.5 flex items-center justify-between gap-3">
        <span id={labelId} className="text-xs font-medium text-fg-muted">
          Retenção
        </span>
        <div role="radiogroup" aria-label="Unidade da retenção" className="flex gap-3">
          {(
            [
              ['days', 'Dias'],
              ['hours', 'Horas'],
            ] as const
          ).map(([value, label]) => (
            <label key={value} className="flex items-center gap-1.5 text-xs text-fg">
              <input
                type="radio"
                name={`${id}-unit`}
                disabled={readOnly}
                checked={unit === value}
                onChange={() => {
                  onUnit(value);
                }}
                className="size-3.5 accent-[var(--color-accent-strong)]"
              />
              {label}
            </label>
          ))}
        </div>
      </div>
      {unit === 'days' ? (
        <select
          aria-label="Retenção"
          disabled={readOnly}
          value={days}
          onChange={(e) => {
            onDays(e.target.value);
          }}
          aria-describedby={describedBy}
          aria-invalid={invalid}
          className={inputClass}
        >
          {dayOptions.map((d) => (
            <option key={d} value={String(d)}>
              {formatRetention(d * minutesPerDay)}
            </option>
          ))}
        </select>
      ) : (
        <div className="flex items-center gap-1.5">
          <select
            aria-label="Horas de retenção"
            disabled={readOnly}
            value={hours}
            onChange={(e) => {
              onHours(e.target.value);
            }}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            className={inputClass}
          >
            {hourOptions.map((h) => (
              <option key={h} value={String(h)}>
                {String(h)}
              </option>
            ))}
          </select>
          <span className="text-xs text-fg-muted">h</span>
          <select
            aria-label="Minutos de retenção"
            disabled={readOnly}
            value={minutes}
            onChange={(e) => {
              onMinutes(e.target.value);
            }}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            className={inputClass}
          >
            {minuteOptions.map((m) => (
              <option key={m} value={String(m)}>
                {String(m).padStart(2, '0')}
              </option>
            ))}
          </select>
          <span className="text-xs text-fg-muted">min</span>
        </div>
      )}
      {error && (
        <p id={errorId} className="mt-1.5 text-xs text-crit">
          {error}
        </p>
      )}
    </div>
  );
}
